import React from 'react';
import { Box, Stack, Typography, ToggleButton, ToggleButtonGroup } from '@mui/material';

const DARK_BLUE = '#206FA5';
// Hover shade used with the brand blue elsewhere in the Safety Hub.
const DARK_BLUE_HOVER = '#2B5A87';

export type TriState = 'all' | 'yes' | 'no';

interface Props {
    appFilter: TriState;
    deviceFilter: TriState;
    onAppFilterChange(value: TriState): void;
    onDeviceFilterChange(value: TriState): void;
    count: number;
}

const TOGGLE_GROUP_SX = {
    height: 32,
    '& .MuiToggleButtonGroup-grouped': {
        textTransform: 'none',
        fontSize: 12,
        fontWeight: 500,
        color: 'grey.700',
        px: 1.75,
        border: '1px solid',
        borderColor: 'grey.300',
        // Solid fill for the selected segment: the earlier 10% tint read as
        // barely different from the unselected state (Sean, 2026-09-21).
        '&.Mui-selected': {
            color: '#FFFFFF',
            backgroundColor: DARK_BLUE,
            borderColor: DARK_BLUE,
            '&:hover': { backgroundColor: DARK_BLUE_HOVER, borderColor: DARK_BLUE_HOVER },
        },
    },
};

const Label: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Typography
        sx={{
            fontSize: 11,
            fontWeight: 600,
            color: 'grey.600',
            textTransform: 'uppercase',
            letterSpacing: '1px',
        }}
    >
        {children}
    </Typography>
);

export const UsersSecondaryFilterRow: React.FC<Props> = ({
    appFilter,
    deviceFilter,
    onAppFilterChange,
    onDeviceFilterChange,
    count,
}) => {
    return (
        <Box
            sx={{
                px: 3,
                py: 1.5,
                bgcolor: '#FAFBFC',
                borderTop: '1px solid rgba(145,158,171,0.12)',
                borderBottom: '1px solid rgba(145,158,171,0.12)',
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 2,
            }}
        >
            <Stack direction="row" alignItems="center" spacing={1}>
                <Label>Mobile App</Label>
                <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={appFilter}
                    onChange={(_, v) => v && onAppFilterChange(v)}
                    sx={TOGGLE_GROUP_SX}
                >
                    <ToggleButton value="all">All</ToggleButton>
                    <ToggleButton value="yes">Yes</ToggleButton>
                    <ToggleButton value="no">No</ToggleButton>
                </ToggleButtonGroup>
            </Stack>

            <Stack direction="row" alignItems="center" spacing={1}>
                <Label>Device</Label>
                <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={deviceFilter}
                    onChange={(_, v) => v && onDeviceFilterChange(v)}
                    sx={TOGGLE_GROUP_SX}
                >
                    <ToggleButton value="all">All</ToggleButton>
                    <ToggleButton value="yes">Yes</ToggleButton>
                    <ToggleButton value="no">No</ToggleButton>
                </ToggleButtonGroup>
            </Stack>

            <Box sx={{ flexGrow: 1 }} />

            <Typography sx={{ fontSize: 13, color: 'grey.600' }}>
                {count} user{count === 1 ? '' : 's'}
            </Typography>
        </Box>
    );
};
